import { useEffect, useState } from "react";

import {
  resetFiltersTopics,
  setIsLoadingTopics,
  setPageTopicsAndSize,
  setSorting,
  setTopics,
} from "store/slicer/GestioneTopicsSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  buttonView,
  labelDescription,
  labelDi,
  labelElement,
  labelElementsViewed,
  labelFoundIn,
  labelLastUpdate,
  labelNoDescription,
  labelNome,
  labelTopicEmpty,
  labelCreatedBy,
  labelNoResults,
} from "_constants";
import { EmptyMessageTable, IconSortTable } from "components/shared";
import { Dropdown } from "primereact/dropdown";
import { formatDataSource } from "utils/utils";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { handleSort } from "utils";
import { getAllTopics } from "services";
import { useNavigate } from "react-router-dom";
import { TopicsTableRowButtonActions } from "./TopicsTableRowButtonActions";
import { setTopicSources } from "store/slicer/GestioneSourcesSlice";
import { ModalEditTopic } from "components/modals";
import { Topic } from "interfaces";
import { Tooltip } from "primereact/tooltip";

export const TopicsTable = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [topicToEdit, setTopicToEdit] = useState<Topic | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const filters = useAppSelector((state) => state.gestioneTopicsSlice.filters);
  const isLoadingTopics = useAppSelector(
    (state) => state.gestioneTopicsSlice.isLoadingTopics
  );
  const topicsList = useAppSelector(
    (state) => state.gestioneTopicsSlice.topicList
  );
  const sortedDirection = useAppSelector(
    (state) => state.gestioneTopicsSlice.sortedDirection
  );
  const sortedColumn = useAppSelector(
    (state) => state.gestioneTopicsSlice.sortedColumn
  );

  useEffect(() => {
    return () => {
      dispatch(resetFiltersTopics());
    };
  }, [dispatch]);

  useEffect(() => {
    if (filters.pageIndex > -1) {
      dispatch(setIsLoadingTopics(true));
      getAllTopics(filters)
        .then((response) => {
          setTotalCount(response.data.totalCount);
          dispatch(setTopics(response.data.items as any));
        })
        .catch((_) => {})
        .finally(() =>
          setTimeout(() => {
            setFirstLoad(false);
            dispatch(setIsLoadingTopics(false));
          }, 500)
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const onPage = (event: DataTableStateEvent) => {
    if (event.page || event.rows) {
      dispatch(
        setPageTopicsAndSize({
          page: event.page ? event.page + 1 : 1,
          size: event.rows,
        })
      );
    }
  };

  const goToSourcesList = (id: string) => {
    dispatch(setTopicSources(id));
    navigate(`/bo/sources`);
  };

  const callHandleSort = (field: String) => {
    handleSort(sortedColumn, sortedDirection, dispatch, setSorting, field);
  };

  const template2 = {
    layout:
      "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
    RowsPerPageDropdown: (options: any) => {
      const dropdownOptions = [
        { label: 5, value: 5 },
        { label: 10, value: 10 },
        { label: 25, value: 25 },
      ];

      return (
        <Dropdown
          value={options.value}
          options={dropdownOptions}
          onChange={options.onChange}
        />
      );
    },
    CurrentPageReport: (options: any) => {
      const first = (filters.pageIndex - 1) * filters.pageSize + 1;
      const last =
        (filters.pageIndex - 1) * filters.pageSize + filters.pageSize;

      return (
        <>
          <span className="xs-none">
            {labelElementsViewed}
            {first} - {last} {labelDi}
            {totalCount} {labelElement}
            {options.totalRecords > 1 ? "i" : "o"}
          </span>
        </>
      );
    },
  };

  const showEditModalDialog = (topic: Topic) => {
    setTopicToEdit(topic);
    setShowEditModal(true);
  };

  const closeModalEditTopic = () => {
    setTopicToEdit(null);
    setShowEditModal(false);
  };

  const getEmptyMessageTableLabel = () => {
    return isLoadingTopics
      ? ""
      : filters.pageIndex === 1 &&
        !filters.globalSearch?.length &&
        !filters.sorting
      ? labelTopicEmpty
      : labelNoResults;
  };

  return (
    <>
      <DataTable
        value={topicsList}
        size="small"
        lazy
        dataKey="id"
        paginator={topicsList?.length ? true : false}
        rows={filters.pageSize}
        totalRecords={totalCount}
        scrollable
        scrollHeight={`calc(100vh - ${topicsList?.length ? "240" : "180"}px)`}
        onPage={onPage}
        first={filters.pageIndex * filters.pageSize - 1}
        paginatorClassName="flex-container justify-end"
        className={`margin-18 custom-table-topics ${
          topicsList.length ? "has-element" : ""
        }`}
        tableStyle={{ minWidth: topicsList?.length ? "1332px" : "50vw" }}
        emptyMessage={
          !firstLoad ? (
            <EmptyMessageTable label={getEmptyMessageTableLabel()} />
          ) : (
            <div></div>
          )
        }
        paginatorTemplate={template2}
      >
        <Column
          field="name"
          header={
            <IconSortTable
              nomeCampo={labelNome}
              onSortCallback={() =>
                isLoadingTopics ? null : callHandleSort("name")
              }
              iconStatus={
                sortedColumn !== "name"
                  ? null
                  : sortedDirection === "ASC"
                  ? 1
                  : 2
              }
            />
          }
          body={
            isLoadingTopics ? (
              <div className="table-row-skeleton">
                <Skeleton />
              </div>
            ) : (
              (rowData) => (
                <>
                  <Tooltip target={`.name`} mouseTrack mouseTrackLeft={10} />

                  <div
                    className={`name topic-chip`}
                    data-pr-tooltip={rowData.name}
                  >
                    <span className="ellipsis">{rowData.name}</span>
                  </div>
                </>
              )
            )
          }
        />
        <Column
          field="description"
          className="column-description"
          header={
            <IconSortTable
              nomeCampo={labelDescription}
              onSortCallback={() =>
                isLoadingTopics ? null : callHandleSort("description")
              }
              iconStatus={
                sortedColumn !== "description"
                  ? null
                  : sortedDirection === "ASC"
                  ? 1
                  : 2
              }
            />
          }
          body={
            isLoadingTopics ? (
              <div className="table-row-skeleton">
                <Skeleton />
              </div>
            ) : (
              (rowData) => (
                <>
                  {rowData.description?.length ? (
                    rowData.description
                  ) : (
                    <span className="italic">{labelNoDescription} </span>
                  )}
                </>
              )
            )
          }
        />
        <Column
          field="createdBy"
          header={
            <IconSortTable
              nomeCampo={labelCreatedBy}
              onSortCallback={() =>
                isLoadingTopics ? null : callHandleSort("createdBy")
              }
              iconStatus={
                sortedColumn !== "createdBy"
                  ? null
                  : sortedDirection === "ASC"
                  ? 1
                  : 2
              }
            />
          }
          body={
            isLoadingTopics ? (
              <div className="table-row-skeleton">
                <Skeleton />
              </div>
            ) : (
              (rowData) => (
                <>
                  <Tooltip
                    target={`${
                      rowData.createdBy.length > 15 ? ".created-by-name" : ""
                    }`}
                    mouseTrack
                    mouseTrackLeft={10}
                  />

                  <div
                    className={`created-by-name ellipsis fit-content`}
                    data-pr-tooltip={rowData.createdBy}
                  >
                    <span className="capitalize">{rowData.createdBy}</span>
                  </div>
                </>
              )
            )
          }
        />
        <Column
          field="lastUpdate"
          header={
            <IconSortTable
              nomeCampo={labelLastUpdate}
              onSortCallback={() =>
                isLoadingTopics ? null : callHandleSort("lastUpdate")
              }
              iconStatus={
                sortedColumn !== "lastUpdate"
                  ? null
                  : sortedDirection === "ASC"
                  ? 1
                  : 2
              }
            />
          }
          body={
            isLoadingTopics ? (
              <div className="table-row-skeleton">
                <Skeleton />
              </div>
            ) : (
              (rowData) => <>{formatDataSource(rowData.lastUpdate)}</>
            )
          }
        />
        <Column
          field="foundIn"
          header={
            <IconSortTable
              nomeCampo={labelFoundIn}
              onSortCallback={() =>
                isLoadingTopics ? null : callHandleSort("foundIn")
              }
              iconStatus={
                sortedColumn !== "foundIn"
                  ? null
                  : sortedDirection === "ASC"
                  ? 1
                  : 2
              }
            />
          }
          body={
            isLoadingTopics ? (
              <div className="table-row-skeleton">
                <Skeleton />
              </div>
            ) : (
              (rowData) => (
                <div className="flex-container justify-between align-center custom-body-table">
                  <Button
                    outlined
                    onClick={() => goToSourcesList(rowData.id)}
                    className="padd-8 topic-view-button"
                  >
                    <div className="flex-container align-center gap-7">
                      <small className="found-in-topic-badge">
                        {rowData.foundIn}
                      </small>
                      <span className="font-weight-700">{buttonView}</span>
                    </div>
                  </Button>

                  <TopicsTableRowButtonActions
                    topic={rowData}
                    editCallback={showEditModalDialog}
                  />
                </div>
              )
            )
          }
        />
      </DataTable>
      <ModalEditTopic
        showModal={showEditModal}
        topic={topicToEdit!}
        closeModalEditTopicCallback={closeModalEditTopic}
      />
    </>
  );
};
